import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/shared/Input/Input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import useModalStore from "@/store/useModalStore";
import Divider from "@/components/shared/Divider/Divider";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { OrderBump } from "../hooks/useOrderBumps.ts";
import { OrderBumpService } from "@/services/order-bumps.ts";
import { ComboBox } from "@/components/shared/ComboBox/ComboBox";
import { OffersService } from "@/services/offers.service.ts";
import { toast } from "@/hooks/use-toast.ts";
import { isAxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { ProductsService } from "@/services/products.service.ts";

interface EditOrderBumpSheetProps {
  orderBump: OrderBump;
  productId: string;
  modalName: string;
}

export function EditOrderBumpSheet({
  productId,
  orderBump,
  modalName,
}: EditOrderBumpSheetProps) {
  const { t } = useTranslation(["orderBumps", "common"]);

  const orderBumpSchema = z.object({
    product_id: z.string({
      required_error: t("validation.required_product"),
    }),
    offer_id: z.string({
      required_error: t("validation.required_offer"),
    }),
    display_text: z
      .string({
        required_error: t("validation.required_title"),
      })
      .min(1, t("validation.required_title")),
    display_description: z
      .string({
        required_error: t("validation.required_description"),
      })
      .min(1, t("validation.required_description"))
      .max(2000, t("validation.max_description")),
    call_to_action: z
      .string({
        required_error: t("validation.required_call_to_action"),
      })
      .min(1, t("validation.required_call_to_action")),
  });

  type FormData = z.infer<typeof orderBumpSchema>;

  const { closeModal } = useModalStore();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    clearErrors,
  } = useForm<FormData>({
    resolver: zodResolver(orderBumpSchema),
    defaultValues: {
      product_id: orderBump?.offer?.product_id,
      offer_id: orderBump?.offer.id,
      display_text: orderBump?.display_text,
      display_description: orderBump?.display_description,
      call_to_action: orderBump?.call_to_action,
    },
  });

  useEffect(() => {
    if (orderBump) {
      reset({
        product_id: orderBump.offer?.product_id,
        offer_id: orderBump.offer.id,
        display_text: orderBump.display_text,
        display_description: orderBump.display_description,
        call_to_action: orderBump.call_to_action,
      });
    }
  }, [orderBump, productId, reset]);

  const handleClose = () => {
    reset();
    closeModal(modalName);
  };

  const formValues = watch();
  const selectedProductId = watch("product_id");

  const onSubmit = async (data: FormData) => {
    try {
      const formattedData = {
        productId: data.product_id,
        offer_id:
          data.offer_id === "" ? orderBump?.offer?.id || "" : data.offer_id,
        display_text: data.display_text,
        display_description: data.display_description,
        call_to_action: data.call_to_action,
      };

      await OrderBumpService.updateOrderBump({
        orderBumpId: orderBump.id,
        data: formattedData,
        productId,
      });
      handleClose();
    } catch (error) {
      if (isAxiosError(error)) {
        toast({
          description:
            t(error?.response?.data?.message) ||
            t("common:data_submission_error"),
          variant: "error",
        });
      } else {
        toast({
          description: t("common:data_submission_error"),
          variant: "error",
        });
      }
    }
  };

  async function fetchProductOptions(query: string) {
    return await ProductsService.getProductsUpSellOptions(productId, query);
  }

  const fetchOfferOptions = async (query: string) => {
    if (!selectedProductId) return [];

    return await OffersService.getUpsellsOffers({
      productId: selectedProductId,
      q: query || "",
    });
  };

  return (
    <section className="flex flex-col justify-between h-full">
      <SheetHeader className="p-6">
        <SheetTitle className="flex gap-2">{t("edit_order_bump")}</SheetTitle>
      </SheetHeader>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between h-full"
      >
        <section className="flex flex-col gap-4 px-6">
          <div className="space-y-4">
            <ComboBox
              value={formValues.product_id}
              fetchOptions={fetchProductOptions}
              onChange={(value) => {
                clearErrors(["product_id", "offer_id"]);
                setValue("product_id", value);
                setValue("offer_id", "", {
                  shouldValidate: false,
                  shouldDirty: true,
                });
              }}
              label={t("common:product")}
              placeholder={t("common:products_placeholder")}
              helperText={errors.product_id?.message}
            />
            <ComboBox
              key={formValues.product_id}
              fetchOptions={fetchOfferOptions}
              value={formValues.offer_id}
              onChange={(value) => {
                clearErrors("offer_id");
                setValue("offer_id", value);
              }}
              label={t("offer")}
              placeholder={t("select_offer")}
              helperText={errors.offer_id?.message}
              queryKeyExtra={[formValues.product_id]}
              disabled={!selectedProductId}
              isEditing
            />
          </div>

          <Divider />

          <div className="space-y-4 max-sm:mb-4">
            <h3 className="text-lg font-medium text-zinc-100">
              {t("custom_text")}
            </h3>

            <Input
              label={t("display_title")}
              placeholder={t("enter_display_title")}
              {...register("display_text")}
              helperText={errors.display_text?.message}
            />

            <div className="space-y-2">
              <label className="text-sm text-white">{t("description")}</label>
              <Textarea
                placeholder={t("enter_description")}
                className="h-24"
                maxLength={500}
                {...register("display_description")}
              />
              {errors.display_description && (
                <p className="text-sm text-error">
                  {errors.display_description.message}
                </p>
              )}
              <div className="flex justify-end">
                <span className="text-xs text-zinc-500">
                  {watch("display_description")?.length || 0}/500
                </span>
              </div>
            </div>

            <Input
              label={t("call_to_action")}
              {...register("call_to_action")}
              helperText={errors.call_to_action?.message}
            />
          </div>
        </section>

        <section className="flex border-t items-center justify-end gap-4 px-6 py-4 w-full max-sm:pb-12 mt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            className="text-zinc-400 hover:text-zinc-100"
          >
            {t("cancel")}
          </Button>
          <Button disabled={isSubmitting} type="submit">
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="animate-spin" />
                {t("common:saving")}
              </span>
            ) : (
              t("save_changes")
            )}
          </Button>
        </section>
      </form>
    </section>
  );
}
